import type { SearchValueType } from '@myosh/odin-components';
import { Buffer } from 'buffer';
import { isEmpty } from 'lodash';
import type { FieldFilters } from '../@types/common';
import type { FilterCustomProperties, FilterTextProps, FormattingFunction } from '../@types/form-filters';
import type { ExportRecordsQueryProps } from '../@types/records';
import type { UserApiProps } from '../@types/users';
import { createFieldSortText, sortingUsersApiUrl } from './sort-url-builder-utils';
import { forceAssert } from './type-guards';

/**
 * Since JavaScript Date types cannot be serialized we need to convert them to strings, and we use UTC date strings.
 * @param {FieldFilters} filters - The filters to process the dates for.
 * @param {FormattingFunction} formatterFunction - A function that will allow for certain fields to have custom formatting.
 * @returns {FieldFilters} The field filters with the dates converted to UTC.
 */
export function processFieldFilters(filters: FieldFilters = {}, formatterFunction?: FormattingFunction) {
  const processedFilters: FieldFilters = {};
  const filterKeys = Object.keys(filters);
  for (let i = 0, length = filterKeys.length; i < length; i++) {
    const filterValue = filters[filterKeys[i]];
    if (Array.isArray(filterValue.value)) {
      if (filterValue.value.every((item) => item instanceof Date)) {
        const offset = (filterValue.value[0] as Date).getTimezoneOffset();
        processedFilters[filterKeys[i]] = {
          value: filterValue.value
            .map((item) => item as Date)
            .map((date, index) => {
              const correctedDate = new Date(date);
              if (index === 0) {
                correctedDate.setHours(0 - offset / 60, 0, 0, 0);
              } else {
                correctedDate.setHours(23 - offset / 60, 59, 59, 999);
              }
              return formatterFunction
                ? formatterFunction(filterKeys[i], correctedDate) || correctedDate.toISOString()
                : correctedDate.toISOString();
            }) as Array<Date>,
          comparison: filterValue.comparison,
          customDataProperties: filterValue.customDataProperties,
        };
      } else {
        processedFilters[filterKeys[i]] = formatterFunction
          ? {
              value: formatterFunction
                ? (filterValue.value.map(
                    (item) => formatterFunction(filterKeys[i], item) || item
                  ) as Array<SearchValueType>)
                : filterValue.value,
              comparison: filterValue.comparison,
              customDataProperties: filterValue.customDataProperties,
            }
          : filterValue;
      }
    } else if (filterValue.value instanceof Date) {
      const offset = filterValue.value.getTimezoneOffset();
      const correctedDate = new Date(filterValue.value);
      correctedDate.setHours(0 - offset / 60, 0, 0, 0);
      processedFilters[filterKeys[i]] = {
        value: formatterFunction
          ? ((formatterFunction(filterKeys[i], correctedDate) || correctedDate.toISOString()) as SearchValueType)
          : correctedDate.toISOString(),
        comparison: filterValue.comparison,
        customDataProperties: filterValue.customDataProperties,
      };
    } else {
      processedFilters[filterKeys[i]] = formatterFunction
        ? {
            value: (formatterFunction(filterKeys[i], filterValue.value) || filterValue.value) as SearchValueType,
            comparison: filterValue.comparison,
            customDataProperties: filterValue.customDataProperties,
          }
        : filterValue;
    }
  }

  return processedFilters;
}

export function createFieldFilterTextAsFirstQueryParameter(filters?: FieldFilters, isAllForms = false) {
  if (!filters || Object.keys(filters).length === 0) {
    return '';
  }
  return `?${createFieldFilterText({ filters, isFirstParam: true, isAllForms })}`;
}

const fieldFilterFormat = (
  isAllForms: boolean,
  fieldEntityId: number,
  fieldName: string,
  fieldEntityType: string,
  fieldSubType?: string,
  linkedFieldFormat?: string
) => {
  switch (fieldEntityType) {
    case 'FIELD':
      return `field:${fieldEntityId}`;
    case 'KEYWORD':
      return `keyword:${fieldName}`;
    case 'FORM_NAME':
      return 'formId';
    case 'MODULE_NAME':
      return 'moduleId';
    case 'WORKFLOW_STEP':
      return `workflowStep:${fieldEntityId}:transitionDate`;
    case 'HIERARCHY_TYPE':
      return `hierarchy:${fieldEntityId}`;
    case 'HIERARCHY_FIELD':
      return `hierarchyFieldId`;
    case 'RECORD_LINK':
      return fieldSubType === 'HIERARCHY_TYPE'
        ? `RECORDLINK:${fieldEntityId}:HIERARCHY:${linkedFieldFormat}`
        : `RECORDLINK:${fieldEntityId}:${fieldSubType}:${linkedFieldFormat}`;
    case 'REVERSE_RECORD_LINK':
      return fieldSubType === 'HIERARCHY_TYPE'
        ? `REVERSEDRECORDLINK:${fieldEntityId}:HIERARCHY:${linkedFieldFormat}`
        : `REVERSEDRECORDLINK:${fieldEntityId}:${fieldSubType}:${linkedFieldFormat}`;
    case 'QUESTIONNAIRE':
      return `QUESTIONNAIRE:${fieldEntityId}:${fieldSubType}`;
    case 'GRAND_SCORE':
      if (!isAllForms) {
        return `GRANDSCORE:${fieldEntityId}:${fieldSubType}`;
      } else {
        return `field:${fieldEntityId}`;
      }
    default:
      if (fieldName === 'archivedStep' || fieldName === 'lastModifiedDateRange' || fieldName === 'author') {
        return `keyword:${fieldName}`;
      } else if (fieldName === 'defaultForm') {
        return `moduleForm:${fieldName}`;
      } else {
        return fieldName;
      }
  }
};

export const linkedFieldsFormat = (
  linkedEntityType?: string,
  linkedFieldId?: number,
  fieldEntityPropertyName?: string
) => {
  switch (linkedEntityType) {
    case 'FIELD':
      return `${linkedFieldId}`;
    case 'KEYWORD':
      if (fieldEntityPropertyName?.includes('docNo')) {
        return 'docNo';
      } else if (fieldEntityPropertyName?.includes('status')) {
        return 'status';
      } else if (fieldEntityPropertyName?.includes('author')) {
        return 'author';
      } else if (fieldEntityPropertyName?.includes('creationDate')) {
        return 'creationDate';
      } else if (fieldEntityPropertyName?.includes('accountable')) {
        return 'accountable';
      } else if (fieldEntityPropertyName?.includes('version')) {
        return 'version';
      } else if (fieldEntityPropertyName?.includes('linkUrl')) {
        return 'linkUrl';
      } else if (fieldEntityPropertyName?.includes('formName')) {
        return 'formName';
      } else {
        return fieldEntityPropertyName;
      }
    case 'HIERARCHY_TYPE':
      return `${linkedFieldId}`;
    default:
      return fieldEntityPropertyName;
  }
};

// Operators will be updated to the new format on an entity type basis
const filterOperatorFormat = (
  fieldEntityType: string,
  containsBlank = false,
  operatorType: string,
  linkedEntityType?: string
) => {
  if (
    ['FIELD', 'HIERARCHY_TYPE'].includes(fieldEntityType) ||
    (['RECORD_LINK', 'REVERSE_RECORD_LINK'].includes(fieldEntityType) &&
      ['FIELD', 'HIERARCHY_TYPE', 'KEYWORD'].includes(linkedEntityType ?? ''))
  ) {
    return containsBlank ? `${operatorType}:in_with_blank:` : `${operatorType}:in:`;
  } else if (fieldEntityType === 'HIERARCHY_FIELD') {
    return containsBlank ? 'in_with_blank:' : 'in:';
  } else if (fieldEntityType === 'KEYWORD') {
    return containsBlank ? `${operatorType}:in_with_blank:` : `${operatorType}:in:`;
  } else if (operatorType === 'id') {
    // NOTE: this is a deprecated format (https://myosh.atlassian.net/browse/MYOSH-4114)
    if (fieldEntityType === 'ADMIN_FIELD') {
      return containsBlank ? 'id:in_with_blank:' : 'id:in:';
    } else if (['FORM_NAME', 'MODULE_NAME'].includes(fieldEntityType)) {
      return 'in:';
    } else {
      return containsBlank ? 'id_in_with_blank:' : 'id_in:';
    }
  } else if (operatorType === 'caption') {
    if (fieldEntityType === 'ADMIN_FIELD') {
      return containsBlank ? 'in_with_blank:' : 'in:';
    } else {
      return 'in:';
    }
  }
};

export function createFieldFilterText({
  filters,
  isFirstParam = false,
  isAllForms = false,
  captionValuesBase64encoded = false,
}: FilterTextProps) {
  if (!filters || Object.keys(filters).length === 0) {
    return '';
  }
  let filterText = '';
  let valueWasEncoded = false;

  const filterKeys = Object.keys(filters);
  for (let i = 0, length = filterKeys.length; i < length; i++) {
    const fieldName = filterKeys[i];
    const filterComparisonValue = filters[fieldName];
    const { customDataProperties = {} } = filters[fieldName];
    const {
      entityId,
      entityType,
      entityPropertyName,
      linkedFieldId,
      linkedEntityType: linkedFieldSubType,
      grandScoreFieldItemType: grandScoreSubType,
      questionRecordListFieldTypes: questionnaireSubType,
      ignoreBlankFilter,
    } = forceAssert<FilterCustomProperties>(customDataProperties);
    const fieldSubType = grandScoreSubType ?? questionnaireSubType ?? linkedFieldSubType;
    const linkedFieldFormat = linkedFieldsFormat(linkedFieldSubType, linkedFieldId, entityPropertyName);
    const filterFormat = fieldFilterFormat(
      isAllForms,
      entityId,
      fieldName,
      entityType,
      fieldSubType,
      linkedFieldFormat
    );
    if (Array.isArray(filterComparisonValue.value)) {
      const filterValues = ignoreBlankFilter
        ? filterComparisonValue.value.filter((item) => item !== 'blank')
        : filterComparisonValue.value;

      if (filterComparisonValue.comparison === 'between' && filterValues.length === 2) {
        filterText += `&filter=${filterFormat}:gte:${filterValues[0]}&filter=${filterFormat}:lte:${filterValues[1]}`;
      } else if (filterComparisonValue.comparison === 'contains' && filterValues.length > 0) {
        const isOnlyBlankFilter = filterValues.length === 1 && filterValues[0] === 'blank';
        const containsBlank = !isOnlyBlankFilter && filterValues.includes('blank');
        const arrayWithoutBlank = filterValues.filter((value) => value !== 'blank');
        const operatorType = typeof arrayWithoutBlank[0] === 'number' ? 'id' : 'caption';
        const operatorFormat =
          !isOnlyBlankFilter && filterOperatorFormat(entityType, containsBlank, operatorType, linkedFieldSubType);
        const convertFilterValues = (value: SearchValueType) => {
          if (entityType === 'FIELD' && captionValuesBase64encoded && operatorType !== 'id' && !isOnlyBlankFilter) {
            valueWasEncoded = true;
            return Buffer.from(String(value)).toString('base64');
          }
          return value;
        };

        if (isOnlyBlankFilter) {
          if (entityType === 'ADMIN_FIELD' && entityPropertyName === 'affiliation') {
            filterText += `&filter=${filterFormat}:is_blank:true`;
          } else {
            filterText += `&filter=${filterFormat}:id:is_blank:true`;
          }
        } else if (containsBlank) {
          filterText += `&filter=${filterFormat}:${operatorFormat}${arrayWithoutBlank
            .map(convertFilterValues)
            .join(',')}`;
        } else {
          filterText += `&filter=${filterFormat}:${operatorFormat}${filterValues.map(convertFilterValues).join(',')}`;
        }
      } else if (filterComparisonValue.comparison === '=') {
        if (typeof filterComparisonValue.value[0] === 'boolean') {
          if (filterValues.length === 1) {
            filterText += `&filter=${filterFormat}:eq:${filterValues[0]}`;
          }
        } else {
          filterText += `&filter=${filterFormat}:eq:${filterValues}`;
        }
      }
    } else {
      if (typeof filterComparisonValue.value === 'string') {
        if (filterComparisonValue.comparison === 'contains') {
          filterText += `&filter=${filterFormat}:like:${filterComparisonValue.value}`;
        } else if (filterComparisonValue.comparison === '=') {
          filterText += `&filter=${filterFormat}:eq:${filterComparisonValue.value}`;
        } else if (filterComparisonValue.comparison === '>=') {
          filterText += `&filter=${filterFormat}:gte:${filterComparisonValue.value}`;
        } else if (filterComparisonValue.comparison === '<=') {
          filterText += `&filter=${filterFormat}:lte:${filterComparisonValue.value}`;
        }
      } else if (typeof filterComparisonValue.value === 'number') {
        if (filterComparisonValue.comparison === '=') {
          filterText += `&filter=${filterFormat}:eq:${filterComparisonValue.value.toString()}`;
        } else if (filterComparisonValue.comparison === '>') {
          filterText += `&filter=${filterFormat}:gt:${filterComparisonValue.value.toString()}`;
        } else if (filterComparisonValue.comparison === '<') {
          filterText += `&filter=${filterFormat}:lt:${filterComparisonValue.value.toString()}`;
        } else if (filterComparisonValue.comparison === '>=') {
          filterText += `&filter=${filterFormat}:gte:${filterComparisonValue.value.toString()}`;
        } else if (filterComparisonValue.comparison === '<=') {
          filterText += `&filter=${filterFormat}:lte:${filterComparisonValue.value.toString()}`;
        }
      } else if (typeof filterComparisonValue.value === 'boolean') {
        if (filterComparisonValue.comparison === '=') {
          filterText += `&filter=${filterFormat}:eq:${filterComparisonValue.value}`;
        } else if (filterComparisonValue.comparison === 'is_blank') {
          filterText += `&filter=${filterFormat}:is_blank:${filterComparisonValue.value}`;
        }
      }
    }
  }

  const encodedParam = valueWasEncoded ? '&captionValuesBase64encoded=true' : '';
  return isFirstParam ? `${filterText.substring(1)}${encodedParam}` : `${filterText}${encodedParam}`;
}

export function createSummaryLinkedRecordsFilterText(linkedRecordIds?: number[]) {
  return linkedRecordIds ? `?linkedRecordIds=${linkedRecordIds}` : '';
}

export const appendOptionalUserQueryArguments = ({
  fields,
  archivedUsers,
  sortedField,
  filters,
  sort,
}: UserApiProps) => {
  let queryArguments = '';

  if (fields) {
    queryArguments += `&fields=${fields}`;
  }

  if (archivedUsers === true || archivedUsers === false) {
    queryArguments += `&filter=archived:eq:${archivedUsers}`;
  }
  if (filters) {
    queryArguments += `${createFieldFilterText({ filters })}`;
  }

  if (sort) {
    queryArguments += sortingUsersApiUrl(sort);
  }

  if (sortedField) {
    queryArguments += `&sort=${sortedField}`;
  }

  return queryArguments;
};

export const buildGlobalHierarchyFiltersUrl = (
  globalFilters?: Record<number, number[]>,
  globalFiltersMatchingOption?: string
) => {
  if (globalFilters && globalFiltersMatchingOption && globalFiltersMatchingOption.length > 0) {
    let filterString = '';
    const keys = Object.keys(globalFilters);
    for (let i = 0, length = keys.length; i < length; i++) {
      const typeId = keys[i];
      const ids = globalFilters[Number(typeId)];

      filterString += `&filter=globalHierarchy:${typeId}:id:in:${ids.join(',')}`;
    }
    return `${filterString}&globalHierarchyMatch=${globalFiltersMatchingOption}`;
  }
};

export function exportRecordURLConstruct(url: string, args?: ExportRecordsQueryProps) {
  if (!args) {
    return url;
  }
  if (args?.columns) {
    url += `&columns=${args.columns}`;
  }
  if (args.filters) {
    url += `${createFieldFilterText({ filters: args.filters, captionValuesBase64encoded: args.captionValuesBase64encoded })}`;
  }
  if (args.sortDirective) {
    url += args.sortDirective;
  }
  if (!isEmpty(args.sorts)) {
    url += `${createFieldSortText(args.sorts)}`;
  }
  if (args.globalHierarchyFilters) {
    url += `${args.globalHierarchyFilters}`;
  }

  return url;
}
